import React, { useMemo, useState, useEffect, Dispatch, SetStateAction } from 'react'
import { Input, Button, TransactionChecklist, LoaderOverlay } from '../../lib/components'
import { InvestV2WalletFundStep } from './invest-v2-wallet-fund-step'
import { WalletSummary, WalletTopup } from '../../services/wallet-service'
import { LegalAgreement } from '../invest/legal-agreement'
import PopupBlockedWarningModal from '../../lib/components/PopupBlockedWarningModal/popup-blocked-warning-modal'
import { convertToNumber, convertNumberWithCommas } from '../../lib/utils/helpers'
import { InvestV2Stepper } from './invest-v2-stepper'
import { InvestV2DealRecap } from './invest-v2-deal-recap'
import { InvestV2SupportBanner } from './invest-v2-support-banner'
import {
  INVEST_V2_STEPS,
  labelInterestIncomePreference,
  showInterestIncomePreferenceOption,
  getRemainingCanInvestAmountUsd,
  exceedsRemainingInvestmentLimit,
  formatUsdAmountFromNumber,
  formatUsdAmountTyping,
  isUsdAmountTypingInProgress,
} from './invest-v2-utils'
import { InvestV2HintShieldIcon } from './invest-v2-icons'
import { InvestV2InvestmentRisksInline } from './invest-v2-investment-risks-inline'
import { InvestV2TermsheetInline } from './invest-v2-termsheet-inline'
import {
  OpportunityDetails,
  InterestIncomePreference,
  Investment,
  UserInvestmentDetails,
} from '../../lib/types'
import { whatsAppService } from '../../services/whatsapp-support'
import './invest-v2.scss'
import { InvestV2ShareCalculator } from './invest-v2-share-calculator'
import type { ShareCalculatorChange, SharePricing } from './invest-v2-share-order'

const INVEST_V2_LEGAL_PAGE_HEADER = 'Terms of Business'

export type InvestV2PresenterProps = {
  wizardStep: number
  opportunity: OpportunityDetails
  userInvestmentDetail: UserInvestmentDetails | undefined
  investmentValue: number
  setInvestmentValue: (n: number) => void
  /** Set for share-priced deals; switches the amount step to the share calculator. */
  sharePricing: SharePricing | null
  onShareOrderChange: (change: ShareCalculatorChange) => void
  interestIncomePreference: InterestIncomePreference
  setInterestIncomePreference: (v: InterestIncomePreference) => void
  investmentValueForTermsheet: number
  investmentData: Investment | undefined
  investmentEssentialsAck: boolean
  onInvestmentEssentialsChange: (checked: boolean) => void | Promise<void>
  onSignTermsheet: () => void | Promise<void>
  onReopenTermsheetSigning: () => void
  onDownloadSignedTermsheet: () => void
  onTermsheetSigningComplete: () => void
  termsheetDownloadLink: string | null
  termsheetSigningLink: string | null
  termsheetRequiresResign: boolean
  termsheetResignMessage?: string
  isTermsheetSigningPending: boolean
  acceptTOBCheckbox: boolean
  setAcceptTOBCheckbox: Dispatch<SetStateAction<boolean>>
  isTransactionChecklistChecked: boolean
  setIsTransactionChecklistChecked: (v: boolean) => void
  showTransactionChecklistModal: boolean
  setTransactionChecklistModal: (v: boolean) => void
  isTermsheetAgreementChecked: boolean
  isTermsheetAgreementNeeded: boolean
  showPopupBlockedModal: boolean
  isTransitioning: boolean
  /** Risk/term sheet API in flight — disables checkboxes without full-page loader */
  isInlineAckLoading: boolean
  isPrimaryDisabled: () => boolean
  setIsApiError: (v: boolean) => void
  backToProjectHandler: () => void | Promise<void>
  onWizardBack: () => void
  onAmountContinue: () => void
  onReviewContinue: () => void | Promise<void>
  transferPageEnabled: boolean
  wallet?: WalletSummary
  pendingTopup?: WalletTopup
  onPayFromWallet: () => Promise<void>
  onRefreshWallet: () => Promise<void>
  onRequestLimitExtension: () => void
  hasPendingLimitRequest?: boolean
}

const InvestV2Presenter = (props: InvestV2PresenterProps) => {
  const {
    wizardStep,
    opportunity,
    userInvestmentDetail,
    investmentValue,
    setInvestmentValue,
    sharePricing,
    onShareOrderChange,
    interestIncomePreference,
    setInterestIncomePreference,
    investmentData,
    setIsApiError,
    backToProjectHandler,
    onWizardBack,
    onAmountContinue,
    onReviewContinue,
    onInvestmentEssentialsChange,
    onSignTermsheet,
    onReopenTermsheetSigning,
    onDownloadSignedTermsheet,
    onTermsheetSigningComplete,
    termsheetDownloadLink,
    termsheetSigningLink,
    termsheetRequiresResign,
    termsheetResignMessage,
    isTermsheetSigningPending,
    transferPageEnabled,
    isPrimaryDisabled,
    isTransitioning,
    isTransactionChecklistChecked,
    setIsTransactionChecklistChecked,
    showTransactionChecklistModal,
    setTransactionChecklistModal,
    isTermsheetAgreementNeeded,
    isTermsheetAgreementChecked,
    acceptTOBCheckbox,
    setAcceptTOBCheckbox,
    showPopupBlockedModal,
    investmentEssentialsAck,
    isInlineAckLoading,
    wallet,
    pendingTopup,
    onPayFromWallet,
    onRefreshWallet,
    onRequestLimitExtension,
    hasPendingLimitRequest = false,
  } = props

  const isCheckboxBusy = isTransitioning || isInlineAckLoading

  const [amountDisplay, setAmountDisplay] = useState('0')

  const canInvestAmount = useMemo(
    () => getRemainingCanInvestAmountUsd(userInvestmentDetail?.canInvest, investmentData?.amount),
    [userInvestmentDetail?.canInvest, investmentData?.amount],
  )

  const investmentLimitUnset = !(Number(userInvestmentDetail?.investmentLimit) > 0)

  const isOverInvestmentLimit =
    !investmentLimitUnset && exceedsRemainingInvestmentLimit(investmentValue, canInvestAmount)

  useEffect(() => {
    setAmountDisplay((prev) => {
      if (isUsdAmountTypingInProgress(prev, investmentValue)) {
        return formatUsdAmountTyping(prev.replace(/,/g, ''))
      }
      return formatUsdAmountFromNumber(investmentValue)
    })
  }, [investmentValue])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [wizardStep])

  const handleInvestErrorMessage = (val: number): { investmentAmount?: string } => {
    let maxInvestAmount = canInvestAmount
    if (opportunity?.raiseGoal && opportunity.raiseGoal !== '0' && opportunity?.committedAmount) {
      const remaining =
        (parseInt(opportunity.raiseGoal, 10) - parseInt(opportunity.committedAmount ?? '0', 10)) /
        100
      maxInvestAmount = Math.min(remaining, canInvestAmount)
    }
    const ticketSize = parseInt(opportunity?.ticketSize ?? '0', 10) / 100
    // Investment-limit overage is shown in the banner below — avoid a duplicate field error.
    if (exceedsRemainingInvestmentLimit(val, canInvestAmount)) {
      return {}
    }
    if (val > maxInvestAmount) {
      return {
        investmentAmount: `Amount must be less than or equal to $${convertNumberWithCommas(
          maxInvestAmount.toString(),
        )}`,
      }
    }
    // Share-priced deals validate the minimum on the final (incl. fees) amount in the calculator.
    if (!sharePricing && val < ticketSize) {
      return {
        investmentAmount: `Amount must be greater than or equal to $${convertNumberWithCommas(
          ticketSize.toString(),
        )}`,
      }
    }
    return {}
  }

  const recapVariant = wizardStep === 1 ? 'amount' : wizardStep === 2 ? 'review' : 'fund'
  const showInterestIncomePreference = showInterestIncomePreferenceOption(opportunity)

  const loaderMessage =
    wizardStep === 1
      ? 'Saving investment amount...'
      : wizardStep === 2
      ? 'Saving agreement...'
      : wizardStep === 3
      ? 'Processing payment...'
      : 'Please wait...'

  return (
    <div className='invest-v2' data-testid='invest-v2-page'>
      <div className='invest-v2__inner'>
        <InvestV2Stepper activeStep={wizardStep} steps={INVEST_V2_STEPS} />

        <div className='invest-v2__grid'>
          <InvestV2DealRecap
            opportunity={opportunity}
            investmentData={investmentData}
            variant={recapVariant}
          />

          <div className='invest-v2__main'>
            {wizardStep === 1 && (
              <section className='invest-v2__card' data-testid='invest-v2-step-amount'>
                <h1 className='invest-v2__title'>Investment Amount</h1>
                <p className='invest-v2__lead'>
                  Specify the total capital commitment you wish to make to the fund.
                </p>
                {sharePricing ? (
                  <InvestV2ShareCalculator
                    key={investmentData?.id ?? 'new'}
                    pricing={sharePricing}
                    initialShares={investmentData?.shares ?? undefined}
                    onChange={onShareOrderChange}
                    externalError={
                      investmentValue > 0
                        ? handleInvestErrorMessage(investmentValue).investmentAmount
                        : undefined
                    }
                  />
                ) : (
                  <>
                    <label className='invest-v2__field-label'>Enter Total Commitment</label>
                    <div className='invest-v2__money-input'>
                      <div className='invest-v2__money-input-row'>
                        <span className='invest-v2__money-prefix'>
                          {opportunity.currencyCode || 'USD'}
                        </span>
                        <div className='invest-v2__money-input-field'>
                          <Input
                            type='text'
                            name='investmentAmount'
                            placeholder='0.00'
                            inputMode='decimal'
                            data-testid='invest-v2-amount-input'
                            value={amountDisplay}
                            handleChange={(e) => {
                              const formatted = formatUsdAmountTyping(e.target.value)
                              setAmountDisplay(formatted)
                              setInvestmentValue(convertToNumber(formatted))
                              setIsApiError(false)
                            }}
                            maxValue={canInvestAmount}
                            errors={handleInvestErrorMessage(investmentValue)}
                            touched={{ investmentAmount: true }}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
                <p className='invest-v2__hint'>
                  <span className='invest-v2__hint-icon' aria-hidden>
                    <InvestV2HintShieldIcon className='invest-v2__hint-icon-svg' />
                  </span>
                  <span>
                    Minimum investment:{' '}
                    <strong>
                      $
                      {convertNumberWithCommas(
                        (parseInt(opportunity.ticketSize, 10) / 100).toString(),
                      )}
                    </strong>
                    {investmentLimitUnset ? (
                      <>
                        . Your investment limit has not yet been set; capacity is currently
                        unavailable.
                      </>
                    ) : (
                      <>
                        . Remaining investment limit:{' '}
                        <strong>${convertNumberWithCommas(canInvestAmount.toString())}</strong>
                      </>
                    )}
                  </span>
                </p>
                {investmentLimitUnset ? (
                  <div
                    className='invest-v2__info-banner invest-v2__info-banner--limit-warning'
                    data-testid='invest-v2-limit-unset'
                    role='alert'
                  >
                    <div className='invest-v2__info-banner-copy'>
                      <p className='invest-v2__info-banner-title--strong'>
                        Your investment limit has not yet been set; capacity is currently
                        unavailable.
                      </p>
                    </div>
                  </div>
                ) : null}
                {isOverInvestmentLimit && (
                  <div
                    className='invest-v2__info-banner invest-v2__info-banner--limit-warning'
                    data-testid='invest-v2-limit-warning'
                    role='alert'
                  >
                    <div className='invest-v2__info-banner-copy'>
                      <p className='invest-v2__info-banner-text invest-v2__info-banner-text--error'>
                        The requested amount exceeds the remaining available investment limit. You
                        can invest up to{' '}
                        <strong>${convertNumberWithCommas(canInvestAmount.toString())}</strong>.
                        Lower the amount to continue
                        {hasPendingLimitRequest ? (
                          <>, or wait — your request is under review.</>
                        ) : (
                          <>
                            , or{' '}
                            <button
                              type='button'
                              className='invest-v2__inline-link'
                              onClick={onRequestLimitExtension}
                              data-testid='invest-v2-request-limit-extension'
                            >
                              place a request to increase the Investment Limit
                            </button>
                            .
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                )}
                {showInterestIncomePreference && (
                  <fieldset className='invest-v2__interest-pref'>
                    <legend className='invest-v2__field-label'>Interest on your investment</legend>
                    <p className='invest-v2__interest-pref-lead'>
                      Choose whether accrued interest is paid out to your bank or reinvested.
                    </p>
                    <div
                      className='invest-v2__segmented'
                      role='radiogroup'
                      aria-label='Interest preference'
                    >
                      <button
                        type='button'
                        role='radio'
                        aria-checked={interestIncomePreference === 'payout'}
                        className={
                          interestIncomePreference === 'payout'
                            ? 'invest-v2__segmented-btn invest-v2__segmented-btn--active'
                            : 'invest-v2__segmented-btn'
                        }
                        onClick={() => {
                          setInterestIncomePreference('payout')
                          setIsApiError(false)
                        }}
                        data-testid='invest-v2-interest-payout'
                      >
                        Payout
                      </button>
                      <button
                        type='button'
                        role='radio'
                        aria-checked={interestIncomePreference === 'reinvestment'}
                        className={
                          interestIncomePreference === 'reinvestment'
                            ? 'invest-v2__segmented-btn invest-v2__segmented-btn--active'
                            : 'invest-v2__segmented-btn'
                        }
                        onClick={() => {
                          setInterestIncomePreference('reinvestment')
                          setIsApiError(false)
                        }}
                        data-testid='invest-v2-interest-reinvestment'
                      >
                        Reinvestment
                      </button>
                    </div>
                    <p className='invest-v2__interest-pref-note'>
                      Current selection:{' '}
                      <strong>{labelInterestIncomePreference(interestIncomePreference)}</strong>
                    </p>
                  </fieldset>
                )}
                <div className='invest-v2__actions invest-v2__actions--split'>
                  <button
                    type='button'
                    className='invest-v2__link-btn'
                    onClick={() => void backToProjectHandler()}
                    data-testid='invest-v2-cancel'
                  >
                    ← Cancel
                  </button>
                  <Button
                    text='Continue →'
                    variant='primary-btn'
                    isDisabled={isPrimaryDisabled()}
                    handleClick={onAmountContinue}
                    classes={['invest-v2__btn-primary']}
                    testId='invest-v2-continue-amount'
                  />
                </div>
              </section>
            )}

            {wizardStep === 2 && (
              <section className='invest-v2__card' data-testid='invest-v2-step-review'>
                <h1 className='invest-v2__title'>Review</h1>
                <p className='invest-v2__lead'>
                  Please review and accept the investment risks and terms of business. If a term
                  sheet applies, sign it electronically before continuing.
                </p>
                <InvestV2InvestmentRisksInline
                  acknowledged={investmentEssentialsAck}
                  onCheckedChange={onInvestmentEssentialsChange}
                  isSubmitting={isCheckboxBusy}
                />
                {isTermsheetAgreementNeeded && (
                  <InvestV2TermsheetInline
                    signed={isTermsheetAgreementChecked}
                    isSigningPending={isTermsheetSigningPending}
                    isLoading={isCheckboxBusy && !isTermsheetSigningPending}
                    downloadLink={termsheetDownloadLink}
                    signingLink={termsheetSigningLink}
                    requiresResign={termsheetRequiresResign}
                    resignMessage={termsheetResignMessage}
                    onSign={onSignTermsheet}
                    onReopenSigning={onReopenTermsheetSigning}
                    onDownloadSignedTermsheet={onDownloadSignedTermsheet}
                    onSigningComplete={onTermsheetSigningComplete}
                  />
                )}
                <div className='invest-v2__legal-wrap'>
                  <LegalAgreement
                    acceptTOBCheckbox={acceptTOBCheckbox}
                    setAcceptTOBCheckbox={setAcceptTOBCheckbox}
                    opportunity={opportunity}
                    pageHeaderText={INVEST_V2_LEGAL_PAGE_HEADER}
                    variant='invest-v2'
                    isSubmitting={isCheckboxBusy}
                  />
                </div>
                <div className='invest-v2__actions invest-v2__actions--split'>
                  <button
                    type='button'
                    className='invest-v2__link-btn'
                    onClick={onWizardBack}
                    data-testid='invest-v2-back-review'
                  >
                    ← Back
                  </button>
                  <Button
                    text='Continue →'
                    variant='primary-btn'
                    isDisabled={isPrimaryDisabled()}
                    handleClick={() => void onReviewContinue()}
                    classes={['invest-v2__btn-primary']}
                    testId='invest-v2-continue-review'
                  />
                </div>
              </section>
            )}

            {wizardStep === 3 && investmentData && (
              <InvestV2WalletFundStep
                opportunity={opportunity}
                wallet={wallet}
                investmentAmountCents={
                  investmentData.shares && Number(investmentData.final_amount) > 0
                    ? Number(investmentData.final_amount)
                    : Number(investmentData.amount)
                }
                shareOrder={
                  investmentData.shares
                    ? {
                        shares: investmentData.shares,
                        pricePerShareCents: Number(investmentData.price_per_share),
                        amountCents: Number(investmentData.amount),
                        salesFeeCents: Number(investmentData.sales_fee),
                        finalAmountCents: Number(investmentData.final_amount),
                        feePercentLabel: sharePricing
                          ? String(sharePricing.salesFeeBasisPoints / 100)
                          : undefined,
                      }
                    : undefined
                }
                pendingTopup={pendingTopup}
                isBusy={isTransitioning}
                onPayFromWallet={onPayFromWallet}
                onRefresh={onRefreshWallet}
                onBack={onWizardBack}
              />
            )}
            <LoaderOverlay
              isVisible={isTransitioning}
              message={loaderMessage}
              className='invest-v2__main-loader'
            />
          </div>
        </div>

        <InvestV2SupportBanner
          onContactSupport={() =>
            whatsAppService.requestInvestmentSupport(opportunity.projectName || '')
          }
        />
      </div>

      <TransactionChecklist
        active={
          transferPageEnabled &&
          wizardStep === 3 &&
          (showTransactionChecklistModal || !isTransactionChecklistChecked)
        }
        handleOnClose={() => {
          setTransactionChecklistModal(false)
          setIsTransactionChecklistChecked(true)
        }}
        opportunity={opportunity}
        cardClasses={['invest-v2-transaction-checklist-card']}
        modalContainerClassName='invest-v2-modal-backdrop'
      />
      {showPopupBlockedModal && <PopupBlockedWarningModal />}
    </div>
  )
}

export { InvestV2Presenter }
